import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import IdsColorVariantMixin from '../../mixins/ids-color-variant-mixin/ids-color-variant-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsMenuItem from '../ids-menu/ids-menu-item';

import '../ids-trigger-field/ids-trigger-button';
import '../ids-icon/ids-icon';

import styles from './ids-sub-tab.scss';
import type IdsSubTabs from './ids-sub-tabs';
import IdsTab from './ids-tab';

const Base = IdsColorVariantMixin(
  IdsLocaleMixin(
    IdsEventsMixin(
      IdsMenuItem
    )
  )
);

/**
 * IDS Sub Tab
 * @type {IdsSubTab}
 * @inherits IdsMenuItem
 * @mixes IdsEventsMixin
 * @mixes IdsLocaleMixin
 * @mixes IdsColorVariantMixin
 */
@customElement('ids-sub-tab')
@scss(styles)
export default class IdsSubTab extends Base {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#afterConnectedCallback();
  }

  /**
   * Refresh component's bindings after render
   */
  #afterConnectedCallback() {
    if (this.hasAttribute('dismissible')) {
      this.dismissible = true;
    }
  }

  /**
   * Gets the label attribute value
   * @returns {string | null} The label value or null if not set
   */
  get label(): string | null {
    return this.getAttribute(attributes.LABEL);
  }

  /**
   * Sets the label attribute value
   * @param {string | null} value The label value to set
   */
  set label(value: string | null) {
    if (value) {
      this.setAttribute(attributes.LABEL, value);
    } else {
      this.removeAttribute(attributes.LABEL);
    }
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes as an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.DISABLED,
      attributes.HIDDEN,
      attributes.HIGHLIGHTED,
      attributes.ICON,
      attributes.ICON_ALIGN,
      attributes.INHERIT_COLOR,
      attributes.LABEL,
      attributes.SELECTED,
      attributes.SHORTCUT_KEYS,
      attributes.SUBMENU,
      attributes.TARGET,
      attributes.TABINDEX,
      attributes.TEXT_ALIGN,
      attributes.TOGGLEABLE,
      attributes.VALUE,
      attributes.VIEWBOX
    ];
  }

  /**
   * Inner template contents
   * @returns {string} The template
   */
  template() {
    let disabledClass = '';
    let disabledAttr = '';
    if (this.state?.disabled) {
      disabledClass = ' disabled';
      disabledAttr = ' disabled';
    }

    // Check
    const check = this.templateCheck();

    // Icon
    const iconSlot = `<span class="ids-menu-item-icon" role="presentation"><slot name="icon"></slot></span>`;

    // Selected
    let selectedClass = '';
    if (this.state?.selected) selectedClass = ' selected';

    // Shortcut
    const shortcutSlot = `<span class="ids-menu-item-shortcuts" part="shortcuts"><slot name="shortcuts"></slot></span>`;
    let shortcutClass = '';
    if (this.shortcutKeys) shortcutClass = ' has-shortcuts';

    // Submenu
    let submenuClass = '';
    if (this.submenu) submenuClass = ' has-submenu';

    // Tabindex
    let tabindex = 'tabindex="0"';
    if (this.state?.tabIndex && !this.state?.disabled) tabindex = ` tabindex="${this.state.tabIndex}"`;

    // TextAlign
    let textClass = '';
    if (this.textAlign) textClass = ` text-${this.textAlign}`;

    // Text
    const text = this.textContent ? this.textContent.trim() : '';
    const tooltipText = text.length > 0 && !(/[\n\r]/.test(text)) ? `tooltip="${text}"` : '';
    const textSlot = `<ids-text class="ids-menu-item-text" overflow="ellipsis"${tooltipText}>${!this.label ? '<slot></slot>' : this.label}</ids-text>`;

    let innerContent = `${check}${iconSlot}${textSlot}${shortcutSlot}`;
    if (this.iconAlign === 'end') innerContent = `${check}${textSlot}${iconSlot}${shortcutSlot}`;

    // Main
    return `<div role="none" part="menu-item" class="ids-sub-tab${disabledClass}${selectedClass}${shortcutClass}${submenuClass}${textClass}">
      <a role="menuitem" ${tabindex} ${disabledAttr}>
        ${innerContent}
      </a>
      ${this.label ? '<div class="tab-content"><slot></slot></div>' : ''}
      <slot name="submenu"></slot>
      <div class="ids-tab-trigger-container">
        <slot name="close"></slot>
      </div>
    </div>`;
  }

  /**
   * Gets the elements assigned to the tab content slot
   * @returns {Element[]} Array of elements in the tab content slot
   */
  get tabContents(): Element[] {
    const slot = this.shadowRoot?.querySelector('.tab-content slot') as HTMLSlotElement;
    return slot?.assignedElements() || [];
  }

  /**
   * @param {boolean} val true if the menu item should appear highlighted
   */
  set highlighted(val) {
    const trueVal = stringToBool(val);

    // Don't highlight if the item is disabled.
    if (trueVal && this.disabled) {
      return;
    }

    if (trueVal) {
      this.setAttribute(attributes.HIGHLIGHTED, 'true');
    } else {
      this.removeAttribute(attributes.HIGHLIGHTED);
    }

    this.state.highlighted = trueVal;
    this.container?.classList[trueVal ? 'add' : 'remove']('highlighted');
  }

  /**
   * @returns {boolean} true if the menu item should show a highlight
   */
  get highlighted() {
    return this.state.highlighted;
  }

  /**
   * Provides a reference to a close button, if applicable
   * @returns {HTMLElement | null} IdsTriggerButton
   */
  get dismissibleBtnEl(): any {
    return this.querySelector('ids-trigger-button');
  }

  /**
   * @param {boolean} isDismissible true if this Tab should contain an "X" icon used for dismissal
   */
  set dismissible(isDismissible: boolean | string) {
    if (stringToBool(isDismissible)) {
      this.setAttribute(attributes.DISMISSIBLE, '');
      this.container?.classList.add(attributes.DISMISSIBLE);
      this.insertAdjacentHTML('beforeend', this.#templateDismissible());
    } else {
      this.removeAttribute(attributes.DISMISSIBLE);
      this.container?.classList.remove(attributes.DISMISSIBLE);
      this.dismissibleBtnEl.remove();
    }
  }

  get dismissible(): boolean {
    return stringToBool(this.getAttribute(attributes.DISMISSIBLE));
  }

  /**
   * @returns {boolean} true if this item is currently selected
   */
  get selected() {
    return this.state.selected;
  }

  /**
   * @param {boolean} val true if the item should be selected
   */
  set selected(val) {
    // Determine true state and event names
    const trueVal = stringToBool(val);
    const duringEventName = trueVal ? 'selected' : 'deselected';

    if (this.state.selected !== trueVal) {
      this.state.selected = trueVal;

      if (trueVal) {
        this.setAttribute(attributes.SELECTED, '');
      } else {
        this.removeAttribute(attributes.SELECTED);
      }

      this.container?.classList[trueVal ? 'add' : 'remove']('selected');
      this.a?.setAttribute(htmlAttributes.ARIA_CHECKED, trueVal ? 'true' : 'false');

      // Build/Fire a `selected` event for performing other actions.
      // This event only fires when selection type changes.
      if (trueVal || this.group?.select === 'multiple' || this.toggleable) {
        this.triggerEvent(duringEventName, this, {
          bubbles: true,
          detail: {
            elem: this,
            value: this.value
          }
        });
      }
    }

    // This event always fires
    this.triggerEvent('pick', this, {
      bubbles: true,
      detail: {
        elem: this,
        value: this.value,
        selected: trueVal
      }
    });
  }

  /**
   * @readonly
   * @returns {IdsSubTabs} ['IdsMenuGroup'] reference to the parent IdsMenuGroup component, if one exists.
   */
  get group() {
    return this.closest<IdsSubTabs>('ids-sub-tabs');
  }

  detectSelectability() {
    const check = this.container?.querySelector('span.check');
    this.container?.classList.remove('has-checkmark', 'has-multi-checkmark');
    check?.remove();
    this.a?.setAttribute(htmlAttributes.ROLE, this.hasSubmenu ? 'button' : 'menuitem');
    this.a?.removeAttribute(htmlAttributes.ARIA_CHECKED);
    if (this.iconAlign === 'end') this.container?.classList.add('align-icon-end');
  }

  /**
   * Dismisses this tab, if possible
   */
  dismiss(): void {
    if (this.dismissible) {
      const rootNode = (this.getRootNode() as any).host as IdsTab;
      this.triggerEvent('subtabremove', this, {
        bubbles: true,
        detail: {
          elem: this,
          value: this.value
        }
      });

      rootNode.triggerEvent('subtabremove', rootNode, {
        bubbles: true,
        detail: {
          elem: this,
          value: this.value
        }
      });
    }
  }

  /**
   * @returns {string} draws the dismissible button
   */
  #templateDismissible(): string {
    const colorVariant = `color-variant="${this.#getDismissibleVariant()}"`;
    const inheritColor = this.colorVariant === 'module' ? '' : 'inherit-color';

    return `<ids-trigger-button class="ids-sub-tab-close-button" slot="close" label="Close" ${colorVariant} ${inheritColor}>
      <ids-icon icon="close" size="small"></ids-icon>
    </ids-trigger-button>`;
  }

  #getDismissibleVariant() {
    return this.colorVariant === 'alternate' ? 'alternate' : 'module';
  }
}
